import { useEffect, useRef } from 'react';

/**
 * Corre `onRestore` cuando el navegador devuelve la página desde el **bfcache**
 * (back/forward cache): el usuario se fue a otro sitio y volvió con "atrás".
 *
 * En ese retorno el navegador **no recarga ni re-monta React**: descongela el heap tal
 * como estaba. Ningún efecto vuelve a correr y todo el estado sobrevive, así que un
 * flag de "cargando" que se prendió justo antes de salir del documento queda prendido
 * para siempre — no hay `finally` que lo baje, porque la promesa que lo iba a bajar
 * quedó abandonada al congelarse la página.
 *
 * `pageshow` con `persisted: true` es la única señal de ese descongelado. El `pageshow`
 * de una carga normal llega con `persisted: false` y se ignora: ahí React montó de cero
 * y no hay nada que resetear.
 *
 * Ver ADR-2026-08-11-bfcache-boton-google-colgado.
 */
export function useBfcacheRestore(onRestore: () => void) {
  // El callback se lee por ref para suscribirse UNA sola vez: quien usa el hook
  // normalmente pasa una arrow nueva en cada render, y sin esto el listener se
  // desuscribiría y re-suscribiría en cada uno.
  const cb = useRef(onRestore);
  cb.current = onRestore;

  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) cb.current();
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);
}

export default useBfcacheRestore;
